import React, { useState, useEffect } from "react";
import { IClinicImage } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";
interface Props {
  images: IClinicImage[];
  onImagesChange: (images: IClinicImage[]) => void;
}
const fileToBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString() ?? "");
    reader.onerror = (error) => reject(error);
  });
};
export const ClinicImages: React.FC<Props> = ({ images, onImagesChange }) => {
  const [selectedImage, setSelectedImage] = useState<IClinicImage | null>(
    images[0] || null
  );
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  // Update selectedImage when images change (especially on initial load)
  useEffect(() => {
    if (images && images.length > 0) {
      // If no image is selected or selected image is not in the current images array, select the first one
      const currentSelectedId = selectedImage?.id || selectedImage?._id;
      const isSelectedImageStillAvailable = currentSelectedId && 
        images.find(img => (img.id === currentSelectedId || img._id === currentSelectedId));
      
      if (!selectedImage || !isSelectedImageStillAvailable) {
        setSelectedImage(images[0]);
      }
    } else {
      setSelectedImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  try {
    if (!e.target.files || e.target.files.length === 0) {
      alert("Please select a file to upload.");
      return;
    }

    const file = e.target.files[0];
    const base64String = await fileToBase64(file);

    // Temporary local preview (optimistic)
    const tempImage: IClinicImage = {
      id: `img_${Date.now()}`,
      imageUrl: URL.createObjectURL(file),
      url: URL.createObjectURL(file),
      alt: file.name,
      file,
      base64: base64String,
    };

    // Show preview instantly
    onImagesChange([...images, tempImage]);
    if (!selectedImage) {
      setSelectedImage(tempImage);
    }

    // Upload to server
    const payload = { images: { file: file, base64: base64String } };
    const res = await baseUrl.patch("/client/booking/addClinicImage", payload);

    if (res.data?.success) {
      // ✅ Server upload success — you can refresh images from response
      const updatedImages = res.data.data || [...images, tempImage];
      onImagesChange(updatedImages);
      toast.success("Image uploaded successfully!");
    } else {
      // ❌ API responded but failed
      console.error("Upload failed:", res.data?.message);
      toast.error(res.data?.message || "Failed to upload image. Please try again.");
      // Optionally remove the temp image
      onImagesChange(images);
    }
  } catch (error: any) {
    // ❌ Handle errors gracefully
    console.error("Error uploading image:", error);
    toast.error(
      error.response?.data?.message ||
        "Something went wrong while uploading the image."
    );
    // Revert optimistic update
    onImagesChange(images);
  } finally {
    // Reset input value to allow re-selecting the same file if needed
    e.target.value = "";
  }
};


const handleDelete = async () => {
  if (!deleteCandidate) return;

  try {
    // 🧹 Optimistically update UI before API call
    const newImages = images.filter((img) => img._id !== deleteCandidate);
    onImagesChange(newImages);

    // 🧾 API call
    const res = await baseUrl.delete("/client/booking/deleteClinicImage", {
      headers: {
        imagekey: deleteCandidate,
      },
    });

    if (res.data?.success) {
      // ✅ Successfully deleted
      if (selectedImage?.id === deleteCandidate) {
        setSelectedImage(newImages[0] || null);
      }
      setDeleteCandidate(null);
      toast.success("Image deleted successfully!");
    } else {
      // ❌ API responded but not success
      console.error("Delete failed:", res.data?.message || "Unknown error");
      toast.error(res.data?.message || "Failed to delete image. Please try again.");
      // Revert UI change if failed
      onImagesChange(images);
    }
  } catch (error:any) {
    // ❌ Network / server / unexpected errors
    console.error("Error deleting image:", error);
    toast.error(
      error.response?.data?.message ||
        "Something went wrong while deleting the image."
    );
    // Revert UI change if API failed
    onImagesChange(images);
  } finally {
    setDeleteCandidate(null);
  }
};

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Clinic Images</h2>
        <label className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all cursor-pointer text-xs">
          <UploadIcon sx={{ fontSize: 16 }} />
          Upload
          <input type="file" accept="image/*" hidden onChange={handleUpload} />
        </label>
      </div>

      {selectedImage && (
        <div
          className="mb-3 cursor-pointer rounded-lg overflow-hidden border border-slate-200"
          onClick={() => setShowPreviewModal(true)}
        >
          <img 
            src={selectedImage.imageUrl || selectedImage.url || ""} 
            alt={selectedImage.alt || "Clinic image"} 
            className="w-full h-auto max-h-[350px] object-cover" 
          />
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2.5">
        {images.map((img: any, i) => (
          <div
            key={i}
            className={`relative rounded-md overflow-hidden cursor-pointer border-2 transition-all ${
              selectedImage?.id === img.id ? "border-primary" : "border-transparent"
            }`}
            onClick={() => setSelectedImage(img)}
          >
            <img src={img.imageUrl || img.url || ""} alt={img.alt || "Clinic image"} className="w-full h-[60px] object-cover block" />
            <button
              className="absolute top-1 right-1 bg-white/80 text-red-500 rounded-full p-0 w-5 h-5 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteCandidate(img._id);
              }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </button>
          </div>
        ))}
      </div>

      {/* Full-size Image Preview Modal */}
      {showPreviewModal && selectedImage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-3">
          <div
            className="bg-white rounded-lg shadow-2xl max-w-[90vw] max-h-[90vh] overflow-auto relative p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors z-10"
              onClick={() => setShowPreviewModal(false)}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
            <img src={selectedImage.imageUrl || selectedImage.url || ""} alt={selectedImage.alt || "Clinic image"} className="max-w-full max-h-[calc(90vh-20px)] block" />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Image"
          message="Are you sure you want to delete this image?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}
    </section>
  );
};
